import React, { useState, useEffect } from "react";
import { AlertCircle, CheckCircle2 } from "lucide-react";

interface WoundCareMatrixProps {
  totalUnitsBilled: number; // e.g., from Q-code
  onValidate: (isValid: boolean, defectSize: number, wasteSize: number) => void;
}

// Sentinel `null` enforces the zero-fact policy: the surgeon must enter a
// concrete number; an unfilled field is NEVER treated as "0 cm² documented".
export function WoundCareMatrix({ totalUnitsBilled, onValidate }: WoundCareMatrixProps) {
  const [defectSize, setDefectSize] = useState<number | null>(null);
  const [wasteSize, setWasteSize] = useState<number | null>(null);

  const bothEntered = defectSize !== null && wasteSize !== null;
  const totalDocumented = (defectSize ?? 0) + (wasteSize ?? 0);
  const isValid = bothEntered && totalUnitsBilled > 0 && totalDocumented === totalUnitsBilled;

  useEffect(() => {
    onValidate(isValid, defectSize ?? 0, wasteSize ?? 0);
  }, [isValid, defectSize, wasteSize, onValidate]);

  return (
    <div className="p-6 border-2 border-border rounded-lg bg-muted/30">
      <h3 className="text-lg font-semibold text-foreground mb-1">
        Skin Substitute Reconciliation
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Defect + Waste must equal billed Q-code units exactly (CMS audit gate).
      </p>

      <div className="flex flex-col md:flex-row gap-6 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-2">
            Defect Size (sq cm)
          </label>
          <input
            type="number"
            min="0"
            className="w-full h-14 text-lg border border-input bg-background rounded-md focus:ring-2 focus:ring-ring focus:border-ring px-4 outline-none"
            value={defectSize ?? ""}
            onChange={(e) => setDefectSize(e.target.value === "" ? null : Number(e.target.value))}
            placeholder="e.g. 12"
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-foreground mb-2">
            Amount Wasted (sq cm)
          </label>
          <input
            type="number"
            min="0"
            className="w-full h-14 text-lg border border-input bg-background rounded-md focus:ring-2 focus:ring-ring focus:border-ring px-4 outline-none"
            value={wasteSize ?? ""}
            onChange={(e) => setWasteSize(e.target.value === "" ? null : Number(e.target.value))}
            placeholder="e.g. 8"
          />
        </div>
      </div>

      <div
        className={`p-4 rounded-md flex items-center justify-between ${
          isValid
            ? "bg-primary/10 border border-primary/30"
            : "bg-destructive/10 border border-destructive/30"
        }`}
      >
        <div className="space-y-0.5">
          <p className="text-sm text-foreground">
            Total Billed Units (Q-Code):{" "}
            <strong className="font-semibold">{totalUnitsBilled} sq cm</strong>
          </p>
          <p className="text-sm text-foreground">
            Total Documented:{" "}
            <strong className="font-semibold">{totalDocumented} sq cm</strong>
          </p>
        </div>
        {isValid ? (
          <div className="flex items-center text-primary">
            <CheckCircle2 className="w-5 h-5 mr-2" />
            <span className="text-sm font-bold">Reconciled</span>
          </div>
        ) : (
          <div className="flex items-center text-destructive">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span className="text-sm font-bold">
              Hard Block: Values must match exactly.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
